import { CasesService, OriginalCaseOption } from '@/@types/generated'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import { CircleCheckIcon, ExternalLinkIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import CaseStatusBadge from '../CaseStatusBadge'

interface OriginalCaseFieldProps {
  caseId: number
  value: OriginalCaseOption | null
  onChange: (option: OriginalCaseOption | null) => void
}

export function OriginalCaseField({
  caseId,
  value,
  onChange,
}: OriginalCaseFieldProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<OriginalCaseOption[]>([])
  const [loading, setLoading] = useState(false)

  // Loads the same-USDOT default list on open, then re-queries as the user types,
  // debounced so every keystroke doesn't fire a request.
  useEffect(() => {
    if (value) return
    let cancelled = false
    setLoading(true)
    const handle = setTimeout(
      () => {
        CasesService.searchOriginalCaseCandidates(caseId, query || undefined)
          .then((res) => {
            if (!cancelled) setResults(res.candidates ?? [])
          })
          .finally(() => {
            if (!cancelled) setLoading(false)
          })
      },
      query ? 250 : 0
    )
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [caseId, query, value])

  const penaltySummary = (option: OriginalCaseOption) => {
    if (
      option.penaltyAppliedAmount <= 0 &&
      option.penaltyOutstandingAmount <= 0
    )
      return null
    const total = option.penaltyAppliedAmount + option.penaltyOutstandingAmount
    return option.penaltyOutstandingAmount > 0
      ? `${format.Currency(total)} penalty · ${format.Currency(option.penaltyOutstandingAmount)} outstanding`
      : `${format.Currency(total)} penalty · paid`
  }

  if (value) {
    const summary = penaltySummary(value)
    return (
      <Card>
        <CardContent className="flex flex-col gap-3">
          <div className="flex flex-wrap items-start gap-2">
            <CircleCheckIcon className="text-success size-5 shrink-0" />
            <span className="font-mono font-semibold">{value.code}</span>
            <div className="ml-auto flex w-full justify-end sm:w-auto">
              <CaseStatusBadge status={value.status} />
            </div>
          </div>

          <div className="flex items-end justify-between gap-2">
            <div className="flex flex-col gap-1">
              <span className="text-muted-foreground text-sm">
                Filed: {format.Date(value.filedAt)}
              </span>
              {summary && (
                <span className="text-muted-foreground text-sm">{summary}</span>
              )}
              <a
                href={buildPath('/cases/:caseId', { caseId: String(value.id) })}
                target="_blank"
                rel="noreferrer"
                className="text-primary flex w-fit items-center gap-1 text-sm hover:underline"
              >
                View case
                <ExternalLinkIcon className="size-3" />
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              onClick={() => onChange(null)}
            >
              Change
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Command shouldFilter={false} className="rounded-md border">
        <CommandInput
          placeholder="Case #, company name, or USDOT"
          value={query}
          onValueChange={setQuery}
        />
        <CommandList className="max-h-[212px]">
          {!loading && results.length === 0 && (
            <CommandEmpty>
              {query ? `No cases match "${query}"` : 'No cases found.'}
            </CommandEmpty>
          )}
          {results.length > 0 && (
            <CommandGroup
              heading={
                query ? `${results.length} result(s)` : `Cases for this company`
              }
            >
              {results.map((option) => {
                const disabled = option.deleted
                const summary = penaltySummary(option)
                return (
                  <CommandItem
                    key={option.id}
                    value={String(option.id)}
                    className={!disabled ? 'cursor-pointer' : undefined}
                    disabled={disabled}
                    aria-disabled={disabled}
                    onSelect={() => {
                      if (disabled) return
                      onChange(option)
                    }}
                  >
                    <div className="flex w-full flex-col gap-0.5">
                      <div className="flex w-full items-center gap-2 [&_[data-slot='status-text']]:max-w-48 [&_[data-slot='status-text']]:truncate">
                        <span className="shrink-0 font-mono text-sm font-semibold whitespace-nowrap">
                          {option.code}
                        </span>
                        <CaseStatusBadge status={option.status} />
                        <span className="text-muted-foreground ml-auto min-w-0 self-end truncate text-right text-sm">
                          Filed: {format.Date(option.filedAt)}
                        </span>
                      </div>
                      {disabled && (
                        <span className="text-muted-foreground text-xs">
                          Deleted · not selectable
                        </span>
                      )}
                      {summary && !disabled && (
                        <span className="text-muted-foreground text-xs">
                          {summary}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </div>
  )
}
